import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { avatarSrc, useBdQuery } from "../hooks/useBdApi";
import { Delta } from "../components/Kpi";
import { HorizontalBars, SparkBars } from "../components/Charts";
import TrendCard from "../components/TrendCard";
import { ClientsCard, WorkPlanCard } from "../components/WorkPlanSummary";
import DataTable from "../components/DataTable";
import { AgpOrgStrip } from "../agp/components/AgpPaceStrip";
import { Avatar, Card, ErrorState, Pill, Skeleton, TeamDot } from "../components/Ui";
import SourceTip, { TitleWithSource } from "../components/SourceTip";
import {
  OPP_RECORD_TYPE_META,
  POINT_TYPES,
  formatDate,
  formatDecimal,
  formatNumber,
  formatPointDate,
  moneyOrDash,
  teamColor,
  teamShortName,
} from "../metrics";

// Events card modes: rows of the "All BD Events" report in the selected period
// (by Event Start Date) or the next events from today forward.
const EVENT_MODES = [
  { id: "period", label: "In period" },
  { id: "upcoming", label: "Upcoming" },
];

const EVENT_STATUS_TONE = {
  Confirmed: "success",
  Tentative: "warning",
  Cancelled: "danger",
  Canceled: "danger",
  Postponed: "warning",
  Completed: "neutral",
};

// Registered attendees vs target: bold when the target is met, muted when
// well short of it (under 75%), plain otherwise.
function RegisteredCell({ registered, target }) {
  if (registered === null || registered === undefined) return <span className="bd-muted">—</span>;
  const text = formatNumber(registered);
  if (!target) return text;
  if (registered >= target) return <strong>{text}</strong>;
  if (registered < target * 0.75) return <span className="bd-muted">{text}</span>;
  return text;
}

function TeamCard({ team, trend, search, comparison }) {
  const color = teamColor(team.name);
  return (
    <Link to={`/landmark/bd/team/${team.key}${search}`} className="bd-team-card" style={{ "--team-color": color }}>
      <div className="bd-team-card__head">
        <div>
          <div className="bd-team-card__eyebrow">
            <TeamDot team={team.name} /> {team.name}
          </div>
          <div className="bd-team-card__meta">
            {team.memberCount} members · {team.liveClients} clients ({team.retainedClients} retainer · {team.pplClients} PPL · {team.commissionClients} commission)
          </div>
        </div>
        <span className="bd-team-card__arrow" aria-hidden="true">
          →
        </span>
      </div>
      <div className="bd-team-card__primary">
        <div>
          <span className="bd-team-card__big">{formatDecimal(team.pointValue)}</span>
          <span className="bd-team-card__unit">pts</span>
        </div>
        <SparkBars trend={trend} dataKey="points" color={color} height={34} />
      </div>
      <dl className="bd-team-card__stats">
        <div>
          <dt>Points</dt>
          <dd>{formatNumber(team.points)}</dd>
        </div>
        <div>
          <dt>Booked</dt>
          <dd>{formatNumber(team.meetingsBooked)}</dd>
        </div>
        <div>
          <dt>Held</dt>
          <dd>{formatNumber(team.meetingsHappened)}</dd>
        </div>
        <div>
          <dt>LMV held</dt>
          <dd>{formatNumber(team.lmvHeld)}</dd>
        </div>
      </dl>
    </Link>
  );
}

export default function OverviewPage({ period, meta }) {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useBdQuery("overview", period.apiParams);
  const [eventMode, setEventMode] = useState("period");
  const [activityTeam, setActivityTeam] = useState("all");
  const [leaderboardView, setLeaderboardView] = useState("top"); // top 10 | everyone

  const teamTrends = useMemo(() => (data && data.teamTrends) || {}, [data]);
  const recentActivity = useMemo(() => {
    const rows = (data && data.recentActivity) || [];
    return activityTeam === "all" ? rows : rows.filter((r) => r.team === activityTeam);
  }, [data, activityTeam]);

  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const kpis = data ? data.kpis : null;
  const comparison = data ? data.comparison : null;
  const teams = data ? data.teams : [];
  const eventRows = data ? (eventMode === "upcoming" ? data.upcomingEvents : data.events) || [] : [];
  const showEveryone = leaderboardView === "all";
  const leaderboardRows = data ? (showEveryone ? data.allMembers : data.leaderboard) || [] : [];

  return (
    <div className="bd-page">
      <div className="bd-page__head">
        <div>
          <h1>Business Development</h1>
          <p className="bd-page__lede">
            {loading || !data ? (
              <Skeleton width={260} height={14} />
            ) : (
              <>
                {formatDate(data.period.from)} – {formatDate(data.period.to)}
                {data.period.compare && data.period.previous && (
                  <span className="bd-muted">
                    {" "}
                    · vs {formatDate(data.period.previous.from)} – {formatDate(data.period.previous.to)}
                  </span>
                )}
              </>
            )}
          </p>
        </div>
      </div>

      <div className="bd-wp-hero">
        <WorkPlanCard kpis={kpis} comparison={comparison} weekly={data ? data.weekly : null} period={data ? data.period : null} loading={loading && !data} />
        <ClientsCard
          kpis={kpis}
          lists={data ? data.clientLists : null}
          weekly={data ? data.weekly : null}
          period={data ? data.period : null}
          changes={data ? data.clientChanges : null}
          loading={loading && !data}
        />
      </div>

      <AgpOrgStrip />

      <section className="bd-section">
        <div className="bd-section-title">
          <h2>
            <TitleWithSource
              title="Teams"
              reports={["wpPoints", "tapCallReports", "otherCallReports", "closedWon", "bdClients"]}
              how="Each team card shows that team's share of the report rows in the period: points and meetings booked (BD WP Points), meetings held (TAP + other call reports), LMV meetings held (LMV points by Call Taken On) and live clients by contract type (retainer / PPL / commission only)."
              attribution="Team attribution: points by the Accredited To user's BD team; call reports by the Assigned user's BD team; clients by Landmark Team."
            >
              Teams
            </TitleWithSource>
          </h2>
          <span className="bd-section-title__meta">Click a team to drill into members and clients</span>
        </div>
        <div className="bd-team-grid">
          {loading && !data
            ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={188} radius={14} />)
            : teams.map((t) => <TeamCard key={t.key} team={t} trend={teamTrends[t.name] || []} search={period.search} />)}
        </div>
        {data && data.unassigned && (data.unassigned.meetingsHappened || data.unassigned.wonDeals || data.unassigned.curateDinners || data.unassigned.points) ? (
          <p className="bd-muted bd-unassigned-note">
            Not attributed to a BD team (included in the totals above): {formatNumber(data.unassigned.points)} points · {formatNumber(data.unassigned.meetingsHappened)}{" "}
            meetings held · {formatNumber(data.unassigned.wonDeals)} closed-won deals · {formatNumber(data.unassigned.curateDinners)} Curate dinners
            <SourceTip
              title="Unassigned rows"
              reports={["tapCallReports", "otherCallReports", "closedWon", "curateWon"]}
              how="Rows in the Salesforce reports whose owner is not on a BD team and that carry no Landmark Team (for example Curate dinners closed by other teams, or call reports logged by non-BD users). They count towards the org totals so the dashboard matches the reports, but cannot be shown under a team."
              attribution=""
            />
          </p>
        ) : null}
      </section>

      <div className="bd-grid bd-grid--1-1">
        <TrendCard
          period={period}
          pageTrend={data ? data.trend : []}
          pageGranularity={data ? data.period.granularity : undefined}
          loading={loading && !data}
          scope="org"
          subtitle="All BD teams"
          dataWindow={meta ? meta.dataWindow : null}
        />
        <Card title={<TitleWithSource metric="pointsByType">Points by type</TitleWithSource>} subtitle="Where the activity is coming from">
          {loading && !data ? (
            <Skeleton height={220} />
          ) : (
            <HorizontalBars
              rows={POINT_TYPES.map((t) => ({
                key: t.type,
                name: t.label,
                value: (kpis.byType[t.type] && kpis.byType[t.type].count) || 0,
                color: t.color,
              })).filter((r) => r.value > 0)}
            />
          )}
          {comparison && data.period.compare && (
            <div className="bd-type-deltas">
              {POINT_TYPES.filter((t) => comparison[t.key] && (kpis[t.key] || comparison[t.key].previous)).map((t) => (
                <span key={t.key} className="bd-type-deltas__item">
                  <i style={{ background: t.color }} />
                  {t.short} <Delta comparison={comparison[t.key]} compact />
                </span>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="bd-grid bd-grid--1-1">
        <Card
          title={<TitleWithSource metric="events">BD events</TitleWithSource>}
          subtitle={
            eventMode === "upcoming"
              ? "Next BD events from today · Joint / Curate events hidden · registered = confirmed guests"
              : "BD events starting in the period · Joint / Curate events hidden · registered = confirmed guests"
          }
          actions={
            <div className="bd-trend__modes" style={{ marginBottom: 0 }}>
              {EVENT_MODES.map((m) => (
                <button key={m.id} type="button" className={`bd-chip ${eventMode === m.id ? "is-active" : ""}`} onClick={() => setEventMode(m.id)}>
                  {m.label}
                </button>
              ))}
            </div>
          }
          flush
        >
          <DataTable
            loading={loading && !data}
            rows={eventRows}
            rowKey={(r) => r.sfId}
            dense
            stickyHeader={false}
            emptyTitle={eventMode === "upcoming" ? "No upcoming BD events" : "No BD events in this period"}
            emptyMessage={eventMode === "upcoming" ? "No events with a start date from today onward." : "Switch to Upcoming to see the next scheduled events."}
            columns={[
              {
                key: "name",
                label: "Event",
                sortValue: (r) => r.name,
                render: (r) => (
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="bd-link bd-ellipsis" title={r.name} onClick={(e) => e.stopPropagation()}>
                    {r.name}
                  </a>
                ),
              },
              { key: "eventDate", label: "Date", width: 96, sortValue: (r) => r.eventDate || "", render: (r) => formatDate(r.eventDate, { year: false }) },
              {
                key: "registeredAttendees",
                label: "Registered",
                align: "right",
                width: 92,
                render: (r) => <RegisteredCell registered={r.registeredAttendees} target={r.targetAttendees} />,
              },
              {
                key: "targetAttendees",
                label: "Target",
                align: "right",
                width: 72,
                render: (r) => (r.targetAttendees === null || r.targetAttendees === undefined ? <span className="bd-muted">—</span> : formatNumber(r.targetAttendees)),
              },
              {
                key: "status",
                label: "Status",
                width: 104,
                hideBelow: "lg",
                render: (r) => (r.status ? <Pill tone={EVENT_STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill> : <span className="bd-muted">—</span>),
              },
              { key: "team", label: "Team", hideBelow: "md", sortValue: (r) => r.team, render: (r) => <TeamDot team={r.team} withName /> },
              {
                key: "mc",
                label: "MC",
                hideBelow: "lg",
                sortValue: (r) => (r.mc && r.mc.name) || "",
                render: (r) =>
                  r.mc && r.mc.id ? (
                    <Link to={`/landmark/bd/member/${r.mc.id}${period.search}`} className="bd-link" onClick={(e) => e.stopPropagation()}>
                      {r.mc.name}
                    </Link>
                  ) : (
                    <span className="bd-muted">—</span>
                  ),
              },
            ]}
          />
        </Card>
        <Card
          title={
            <TitleWithSource
              title="Leaderboard"
              reports={["wpPoints"]}
              how="Top 10 BD users by Workplan point value in the period; 'Everyone' lists every active BD team member alphabetically (zero when they have no points in the period). Blues = Client Meeting + PP - Client Meeting points (split shown underneath), TAPs = TAP points, LMVs = LMV points, Booked = all meeting-type points together; all from the 'All BD WP Points' report."
              attribution="Member attribution: points by Accredited To."
            >
              Leaderboard
            </TitleWithSource>
          }
          subtitle={showEveryone ? "Everyone's points in the period, A–Z · click a column to sort" : "Top individuals by point value · meetings booked by type"}
          actions={
            <div className="bd-trend__modes" style={{ marginBottom: 0 }}>
              {[
                { id: "top", label: "Top 10" },
                { id: "all", label: data && data.allMembers ? `Everyone (${data.allMembers.length})` : "Everyone" },
              ].map((m) => (
                <button key={m.id} type="button" className={`bd-chip ${leaderboardView === m.id ? "is-active" : ""}`} onClick={() => setLeaderboardView(m.id)}>
                  {m.label}
                </button>
              ))}
            </div>
          }
          flush
        >
          <DataTable
            key={leaderboardView}
            loading={loading && !data}
            rows={leaderboardRows}
            rowKey={(r) => r.sfUserId}
            onRowClick={(r) => navigate(`/landmark/bd/member/${r.sfUserId}${period.search}`)}
            dense
            stickyHeader={false}
            pageSize={showEveryone ? 25 : 0}
            emptyTitle="No points in this period"
            columns={[
              ...(showEveryone
                ? []
                : [
                    {
                      key: "rank",
                      label: "#",
                      width: 36,
                      sortable: false,
                      render: (r) => <span className="bd-rank">{leaderboardRows.indexOf(r) + 1}</span>,
                    },
                  ]),
              {
                key: "name",
                label: "Member",
                sortValue: (r) => r.name,
                render: (r) => (
                  <span className="bd-person">
                    <Avatar name={r.name} team={r.team} size="sm" src={avatarSrc(r)} />
                    <span className="bd-person__text">
                      <span>{r.name}</span>
                      <small>{teamShortName(r.team)}</small>
                    </span>
                  </span>
                ),
              },
              { key: "pointValue", label: "Pts", metric: "pointValue", align: "right", render: (r) => <strong>{formatDecimal(r.pointValue)}</strong> },
              {
                key: "bluesBooked",
                label: "Blues",
                metric: "bluesBooked",
                align: "right",
                sortValue: (r) => r.bluesBooked || 0,
                render: (r) => (
                  <span className="bd-stack-cell">
                    <span>{formatNumber(r.bluesBooked)}</span>
                    <small className="bd-muted">
                      {formatNumber(r.clientMeetings)} client · {formatNumber(r.ppClientMeetings)} PP
                    </small>
                  </span>
                ),
              },
              { key: "taps", label: "TAPs", metric: "tapsBooked", align: "right", hideBelow: "md", render: (r) => formatNumber(r.taps) },
              { key: "lmv", label: "LMVs", metric: "lmv", align: "right", hideBelow: "md", render: (r) => formatNumber(r.lmv) },
              { key: "meetingsBooked", label: "Booked", metric: "meetingsBooked", align: "right", render: (r) => <strong>{formatNumber(r.meetingsBooked)}</strong> },
            ]}
          />
        </Card>
      </div>

      <Card
        title={<TitleWithSource metric="dealsWon">Closed-won deals</TitleWithSource>}
        subtitle="Closed Won opportunities in the period (Closed Won Oppor / BD Closed Deals / BD Signed Clients / Closed Won Curate reports) · Curate 2 dinners excluded"
        flush
      >
        <DataTable
          loading={loading && !data}
          rows={data ? data.recentDeals || [] : []}
          rowKey={(r) => r.sfId}
          dense
          stickyHeader={false}
          defaultSort={{ key: "closeDate", dir: "desc" }}
          pageSize={15}
          emptyTitle="No closed-won deals in this period"
          columns={[
            {
              key: "name",
              label: "Deal",
              render: (r) => (
                <span className="bd-person__text">
                  <span className="bd-ellipsis" title={r.name}>
                    {r.name}
                  </span>
                  <small>{[r.accountName, r.clientName].filter(Boolean).join(" · ") || "—"}</small>
                </span>
              ),
            },
            { key: "team", label: "Team", hideBelow: "md", sortValue: (r) => r.team || "", render: (r) => (r.team ? <TeamDot team={r.team} withName /> : <span className="bd-muted">—</span>) },
            {
              key: "owner",
              label: "Owner",
              hideBelow: "lg",
              sortValue: (r) => (r.owner && r.owner.name) || "",
              render: (r) =>
                r.owner && r.owner.id ? (
                  <Link to={`/landmark/bd/member/${r.owner.id}${period.search}`} className="bd-link" onClick={(e) => e.stopPropagation()}>
                    {r.owner.name}
                  </Link>
                ) : (
                  <span className="bd-muted">{(r.owner && r.owner.name) || "—"}</span>
                ),
            },
            { key: "closeDate", label: "Closed", metric: "wonDeals", width: 105, sortValue: (r) => r.closeDate, render: (r) => formatDate(r.closeDate, { year: false }) },
            {
              key: "recordTypeName",
              label: "Type",
              width: 120,
              render: (r) => {
                const m = OPP_RECORD_TYPE_META[r.recordTypeName] || { short: r.recordTypeName || "—", tone: "neutral" };
                return (
                  <Pill tone={m.tone} title={`Record Type: ${r.recordTypeName || "—"}`}>
                    {m.short}
                  </Pill>
                );
              },
            },
            { key: "amount", label: "Revenue", metric: "revenue", align: "right", sortValue: (r) => r.amount || 0, render: (r) => moneyOrDash(r.amount, false) },
          ]}
        />
      </Card>

      <Card
        title={<TitleWithSource metric="recentActivity">Recent activity</TitleWithSource>}
        subtitle={activityTeam === "all" ? "Latest BD Workplan points across all teams" : `Latest BD Workplan points · ${activityTeam}`}
        actions={
          <div className="bd-chip-row">
            {[{ id: "all", label: "All teams" }, ...teams.map((t) => ({ id: t.name, label: teamShortName(t.name), color: teamColor(t.name) }))].map((t) => (
              <button key={t.id} type="button" className={`bd-chip ${activityTeam === t.id ? "is-active" : ""}`} onClick={() => setActivityTeam(t.id)}>
                {t.color && <span className="bd-chip__dot" style={{ background: t.color }} />}
                {t.label}
              </button>
            ))}
          </div>
        }
        flush
      >
        <DataTable
          loading={loading && !data}
          rows={recentActivity}
          rowKey={(r) => r.sfId}
          dense
          stickyHeader={false}
          pageSize={25}
          emptyTitle={activityTeam === "all" ? "No activity in this period" : `No ${activityTeam} activity in the latest 100 points`}
          columns={[
            {
              key: period.pointDateField,
              label: period.dateBasis === "accredited" ? "Accredited" : "Booked",
              width: 110,
              render: (r) => formatPointDate(r, period.pointDateField, { year: false }),
              sortValue: (r) => r[period.pointDateField],
            },
            {
              key: "type",
              label: "Type",
              width: 150,
              render: (r) => (
                <Pill color={POINT_TYPES.find((t) => t.type === r.type)?.color}>{r.type}</Pill>
              ),
            },
            { key: "displayName", label: "Activity", render: (r) => <span className="bd-ellipsis" title={r.displayName}>{r.displayName || r.name}</span> },
            {
              key: "accreditedTo",
              label: "Member",
              sortValue: (r) => r.accreditedTo && r.accreditedTo.name,
              render: (r) =>
                r.accreditedTo && r.accreditedTo.id ? (
                  <Link to={`/landmark/bd/member/${r.accreditedTo.id}${period.search}`} className="bd-link" onClick={(e) => e.stopPropagation()}>
                    {r.accreditedTo.name}
                  </Link>
                ) : (
                  "—"
                ),
            },
            { key: "team", label: "Team", hideBelow: "md", render: (r) => <TeamDot team={r.team} withName />, sortValue: (r) => r.team },
            { key: "pointValue", label: "Value", align: "right", width: 70, render: (r) => formatDecimal(r.pointValue) },
          ]}
        />
      </Card>
    </div>
  );
}
